import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { useAuth } from './AuthContext';
import { useUpdateBranding } from './BrandingContext';
import { fetchBranding } from '../auth/brandingClient';
import { C, FONTS } from '../tokens/design';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const UserContext = createContext(null);

function FullPageSpinner({ message }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 24, height: 24, border: `2px solid ${C.b1}`, borderTop: `2px solid ${C.sky}`, borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>{message || 'Loading...'}</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

function ErrorPage({ message, retry }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.red, fontFamily: FONTS.sans, marginBottom: 8 }}>Failed to load profile</div>
        <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16 }}>{message}</div>
        <button onClick={retry} style={{
          background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '8px 20px',
          color: C.t1, fontSize: 12, fontFamily: FONTS.mono, cursor: 'pointer',
        }}>Retry</button>
      </div>
    </div>
  );
}

export function UserProvider({ children }) {
  const { setUserInfo } = useAuth();
  const updateBranding = useUpdateBranding();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    if (!authService.isAuthenticated()) { setLoading(false); return; }
    hasFetched.current = true;

    const loadMspBranding = async (profileData) => {
      // Platform admins keep domain-based branding; MSP users get their MSP branding
      const isPlatAdmin = profileData?.isPlatformAdmin || profileData?.role === 'PlatformAdmin';
      if (isPlatAdmin) return;
      const mspId = profileData?.mspId;
      if (!mspId) return;
      try {
        const data = await fetchBranding({ mspId });
        updateBranding(data);
      } catch (err) {
        console.warn('[UserContext] Failed to load MSP branding:', err.message);
      }
    };

    const fetchProfile = async () => {
      let token = authService.getAccessToken();

      // If token is expired or expiring soon, try refreshing first
      if (authService.isTokenExpiringSoon() && authService.hasRefreshToken()) {
        try {
          await authService.refresh();
          token = authService.getAccessToken();
        } catch {
          authService.clearTokens();
          setLoading(false);
          return;
        }
      }

      try {
        const res = await fetch(`${BASE_URL}/me/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (res.status === 401 && authService.hasRefreshToken()) {
          // Token expired mid-flight — try one refresh
          try {
            await authService.refresh();
            const retryRes = await fetch(`${BASE_URL}/me/profile`, {
              headers: { 'Authorization': `Bearer ${authService.getAccessToken()}` },
            });
            if (!retryRes.ok) throw new Error(`${retryRes.status}`);
            const data = await retryRes.json();
            setProfile(data);
            setUserInfo(data);
            await loadMspBranding(data);
            setLoading(false);
            return;
          } catch {
            authService.clearTokens();
            setLoading(false);
            return;
          }
        }

        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        setProfile(data);
        setUserInfo(data);
        await loadMspBranding(data);
        setLoading(false);
      } catch (err) {
        console.error('[UserContext] Failed to fetch profile:', err.message);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProfile();
  }, [setUserInfo]);

  const isPlatformAdmin = Boolean(
    profile?.isPlatformAdmin ||
    profile?.role === 'PlatformAdmin'
  );

  const isMspUser = Boolean(
    profile?.isMspUser ||
    profile?.role === 'MspAdmin' ||
    profile?.role === 'MSP Admin'
  );

  const isClientUser = Boolean(
    !isPlatformAdmin && !isMspUser && (profile?.clientId || profile?.role === 'ClientAdmin' || profile?.role === 'ClientViewer')
  );

  if (loading) return <FullPageSpinner message="Loading your profile..." />;
  if (error) return <ErrorPage message={`Profile load failed: ${error}`} retry={() => { hasFetched.current = false; setLoading(true); setError(null); }} />;

  return (
    <UserContext.Provider value={{
      profile,
      isPlatformAdmin,
      isMspUser,
      isClientUser,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
}
